import React from "react";
import { MapPin, Phone } from "lucide-react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  FaFacebook,
  FaInstagram,
  FaLinkedin,
  FaWhatsapp
} from "react-icons/fa";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const Footer = () => {
  const location = useLocation();
  const path = location.pathname;
  const { t, i18n } = useTranslation();
  const language = i18n.language;

  const fadeInUpVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  const socialLinks = [
    { 
      icon: <FaFacebook />, 
      url: "https://www.facebook.com/aruba.creative.09",
      color: "text-blue-600 hover:text-blue-800" 
    },
    { 
      icon: <FaWhatsapp />, 
      url: "https://wa.me/message/UXA23MPLONOAP1",
      color: "text-green-500 hover:text-green-600" 
    },
    { 
      icon: <FaInstagram />, 
      url: "https://www.instagram.com/aruba.creative?igsh=MW8ya3E0aW51aGpkcg==",
      color: "text-pink-500 hover:text-pink-700" 
    },
    { 
      icon: <FaLinkedin />, 
      url: "https://www.linkedin.com/company/aruba-creative-09/?viewAsMember=true",
      color: "text-blue-700 hover:text-blue-900" 
    }
  ];

  const quickLinks = [
    { name: t("feature_7_title"), path: "/software-development" },
    { name: t("read_more"), path: "/portfolio" },
        { name: t("privacy_policy"), path: "/privacy-policy" },

  ];

  return (
    <motion.div 
      dir={language === "en" ? "ltr" : "rtl"}
      className="w-full bg-black"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      variants={{
        visible: { transition: { staggerChildren: 0.1 } }
      }}
    >
      <div className="w-full container mx-auto px-5 py-10 xl:px-0">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <motion.div variants={fadeInUpVariants} className="flex flex-col gap-4">
            <h3 className="text-white text-xl font-bold mb-3">Aruba</h3>
            <p className="text-gray-400">
              {t("software_description")}
            </p>
            <div dir="ltr" className={`flex mt-2 ${language === "en" ? "space-x-3" : "space-x-3 flex-row-reverse space-x-reverse"}`}>
              {socialLinks.map((link, index) => (
                <motion.a
                  key={index}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`text-xl ${link.color} transition-all duration-300 transform hover:scale-110`}
                  whileHover={{ y: -3 }}
                >
                  {link.icon}
                </motion.a>
              ))}
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div variants={fadeInUpVariants} className="flex flex-col gap-2">
            <h3 className="text-white text-xl font-bold mb-3">{t("Explore Services")}</h3>
            <ul className="flex flex-col space-y-2">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <Link 
                    to={link.path}
                    className="text-gray-400 hover:text-white transition-colors duration-300"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Contact Info */}
          <motion.div variants={fadeInUpVariants} className="flex flex-col gap-4 col-span-1 md:col-span-2 lg:col-span-2">
            <h3 className="text-white text-xl font-bold mb-3">{t("contact_us")}</h3>
            
            <div className="flex items-center gap-3 group">
              <span className="bg-white rounded-full p-2 group-hover:bg-gray-200 transition-colors duration-300">
                <MapPin className="w-5 h-5 text-black" />
              </span>
              <a
                href="https://www.google.com/maps?ll=35.561452,45.430715&z=16&t=m&hl=en&gl=IQ&mapclient=embed&cid=10702582187719662024"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors duration-300"
              >
                Iraq - Sulaymaniyah - Park tower Mall - 1st floor - NO: 24
              </a>
            </div>

            <div className="flex items-center gap-3 group">
              <span className="bg-white rounded-full p-2 group-hover:bg-gray-200 transition-colors duration-300">
                <Phone className="w-5 h-5 text-black" />
              </span>
              <a 
                href="tel:+9647709990399" 
                dir="ltr"
                className="text-gray-400 hover:text-white transition-colors duration-300"
              >
                +964 770 999 0399
              </a>
            </div>
          </motion.div>
        </div>

        <motion.div 
          variants={fadeInUpVariants}
          dir="ltr"
          className="border-t border-gray-800 mt-10 pt-6 flex flex-col md:flex-row justify-between items-center"
        >
          <span className="text-gray-400 text-center text-sm md:text-base">
            ©{new Date().getFullYear()}{" "}
            <a href="/" className="hover:text-white transition-colors duration-300">
              Aruba™
            </a>
            . All Rights Reserved.
          </span>
          
          <div className="mt-4 md:mt-0">
            <span className="text-gray-400 text-sm">
              Made with ❤️ by Aruba Creative
            </span>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default Footer;
